import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Button,
  ColorInput,
  Group,
  Paper,
  Select,
  Slider,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { DownloadSimpleIcon, ImageIcon, TrashIcon } from '@phosphor-icons/react';
import QRCodeStyling, { type Options } from 'qr-code-styling';
import {
  type EmailInput,
  type QrType,
  type SmsInput,
  type VCardInput,
  type WifiInput,
  SOCIAL_NETWORKS,
  buildEmail,
  buildSms,
  buildTel,
  buildVCard,
  buildWifi,
} from '@/lib/qr-content';
import { useStrings } from '@/i18n/useLang';

type DotStyle = 'square' | 'rounded' | 'dots' | 'extra-rounded';
type Level = 'L' | 'M' | 'Q' | 'H';

const EMPTY_VCARD: VCardInput = {
  firstName: '',
  lastName: '',
  org: '',
  title: '',
  phone: '',
  email: '',
  url: '',
  socials: {},
};

export function QrGenerator() {
  const t = useStrings();

  const [type, setType] = useState<QrType>('text');
  const [text, setText] = useState('https://www.ecosquirrel.de/');
  const [vcard, setVcard] = useState<VCardInput>(EMPTY_VCARD);
  const [wifi, setWifi] = useState<WifiInput>({ ssid: '', password: '', encryption: 'WPA', hidden: false });
  const [email, setEmail] = useState<EmailInput>({ to: '', subject: '', body: '' });
  const [tel, setTel] = useState('');
  const [sms, setSms] = useState<SmsInput>({ number: '', message: '' });

  const [fg, setFg] = useState('#000000'); // black by default for best scannability
  const [bg, setBg] = useState('#ffffff');
  const [dotStyle, setDotStyle] = useState<DotStyle>('rounded');
  const [level, setLevel] = useState<Level>('M');
  const [size, setSize] = useState(256);
  const [logo, setLogo] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const qrRef = useRef<QRCodeStyling | null>(null);

  const data = useMemo(() => {
    switch (type) {
      case 'vcard':
        return buildVCard(vcard);
      case 'wifi':
        return buildWifi(wifi);
      case 'email':
        return buildEmail(email);
      case 'tel':
        return buildTel(tel);
      case 'sms':
        return buildSms(sms);
      default:
        return text;
    }
  }, [type, text, vcard, wifi, email, tel, sms]);

  const hasData = data.trim().length > 0;
  const ecLevel: Level = logo ? 'H' : level; // a logo needs max error correction

  const options = useMemo<Partial<Options>>(
    () => ({
      width: size,
      height: size,
      type: 'svg',
      data: data || ' ',
      margin: 8,
      image: logo ?? undefined,
      qrOptions: { errorCorrectionLevel: ecLevel },
      dotsOptions: { color: fg, type: dotStyle },
      backgroundOptions: { color: bg },
      imageOptions: { crossOrigin: 'anonymous', margin: 6, imageSize: 0.3, hideBackgroundDots: true },
    }),
    [size, data, logo, ecLevel, fg, dotStyle, bg],
  );

  useEffect(() => {
    try {
      if (!hasData) {
        if (containerRef.current) containerRef.current.innerHTML = '';
        qrRef.current = null;
        return;
      }
      if (!qrRef.current) {
        qrRef.current = new QRCodeStyling(options);
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
          qrRef.current.append(containerRef.current);
        }
      } else {
        qrRef.current.update(options);
      }
    } catch {
      // qr-code-styling needs a DOM/canvas; ignore failures (e.g. in tests).
    }
  }, [options, hasData]);

  const onLogo = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => setLogo(typeof reader.result === 'string' ? reader.result : null);
    reader.readAsDataURL(file);
  };

  const download = (extension: 'png' | 'svg') => {
    qrRef.current?.download({ name: 'qrcode', extension });
  };

  const renderFields = () => {
    switch (type) {
      case 'vcard':
        return (
          <>
            <Group grow>
              <TextInput label={t.ui.vcFirst} value={vcard.firstName} onChange={(e) => setVcard({ ...vcard, firstName: e.currentTarget.value })} />
              <TextInput label={t.ui.vcLast} value={vcard.lastName} onChange={(e) => setVcard({ ...vcard, lastName: e.currentTarget.value })} />
            </Group>
            <Group grow>
              <TextInput label={t.ui.vcOrg} value={vcard.org} onChange={(e) => setVcard({ ...vcard, org: e.currentTarget.value })} />
              <TextInput label={t.ui.vcTitle} value={vcard.title} onChange={(e) => setVcard({ ...vcard, title: e.currentTarget.value })} />
            </Group>
            <Group grow>
              <TextInput label={t.ui.vcPhone} value={vcard.phone} onChange={(e) => setVcard({ ...vcard, phone: e.currentTarget.value })} />
              <TextInput label={t.ui.vcEmail} value={vcard.email} onChange={(e) => setVcard({ ...vcard, email: e.currentTarget.value })} />
            </Group>
            <TextInput label={t.ui.vcUrl} value={vcard.url} onChange={(e) => setVcard({ ...vcard, url: e.currentTarget.value })} />

            <Text fw={500} size="sm" mt="xs">
              {t.ui.vcSocial}
            </Text>
            {SOCIAL_NETWORKS.reduce<JSX.Element[]>((rows, net, i) => {
              if (i % 2 === 0) {
                const pair = SOCIAL_NETWORKS.slice(i, i + 2);
                rows.push(
                  <Group grow key={net.key}>
                    {pair.map((n) => (
                      <TextInput
                        key={n.key}
                        label={n.label}
                        placeholder={t.ui.vcSocialPlaceholder}
                        value={vcard.socials[n.key] ?? ''}
                        onChange={(e) =>
                          setVcard({ ...vcard, socials: { ...vcard.socials, [n.key]: e.currentTarget.value } })
                        }
                      />
                    ))}
                  </Group>,
                );
              }
              return rows;
            }, [])}
          </>
        );
      case 'wifi':
        return (
          <>
            <TextInput label={t.ui.wifiSsid} value={wifi.ssid} onChange={(e) => setWifi({ ...wifi, ssid: e.currentTarget.value })} />
            <Group grow align="flex-end">
              <Select
                label={t.ui.wifiEncryption}
                value={wifi.encryption}
                onChange={(v) => v && setWifi({ ...wifi, encryption: v as WifiInput['encryption'] })}
                allowDeselect={false}
                data={[
                  { value: 'WPA', label: 'WPA/WPA2' },
                  { value: 'WEP', label: 'WEP' },
                  { value: 'nopass', label: t.ui.encNone },
                ]}
              />
              {wifi.encryption !== 'nopass' && (
                <TextInput label={t.ui.wifiPassword} value={wifi.password} onChange={(e) => setWifi({ ...wifi, password: e.currentTarget.value })} />
              )}
            </Group>
            <Switch label={t.ui.wifiHidden} checked={wifi.hidden} onChange={(e) => setWifi({ ...wifi, hidden: e.currentTarget.checked })} />
          </>
        );
      case 'email':
        return (
          <>
            <TextInput label={t.ui.emailTo} value={email.to} onChange={(e) => setEmail({ ...email, to: e.currentTarget.value })} />
            <TextInput label={t.ui.emailSubject} value={email.subject} onChange={(e) => setEmail({ ...email, subject: e.currentTarget.value })} />
            <Textarea label={t.ui.emailBody} value={email.body} onChange={(e) => setEmail({ ...email, body: e.currentTarget.value })} autosize minRows={2} />
          </>
        );
      case 'tel':
        return <TextInput label={t.ui.telNumber} value={tel} onChange={(e) => setTel(e.currentTarget.value)} maw={320} />;
      case 'sms':
        return (
          <>
            <TextInput label={t.ui.smsNumber} value={sms.number} onChange={(e) => setSms({ ...sms, number: e.currentTarget.value })} maw={320} />
            <Textarea label={t.ui.smsMessage} value={sms.message} onChange={(e) => setSms({ ...sms, message: e.currentTarget.value })} autosize minRows={2} />
          </>
        );
      default:
        return (
          <Textarea label={t.ui.contentLabel} placeholder={t.ui.contentPlaceholder} value={text} onChange={(e) => setText(e.currentTarget.value)} autosize minRows={2} maxRows={6} />
        );
    }
  };

  return (
    <Stack gap="lg">
      <Select
        label={t.ui.type}
        value={type}
        onChange={(v) => v && setType(v as QrType)}
        allowDeselect={false}
        data={[
          { value: 'text', label: t.ui.typeText },
          { value: 'vcard', label: t.ui.typeVcard },
          { value: 'wifi', label: t.ui.typeWifi },
          { value: 'email', label: t.ui.typeEmail },
          { value: 'tel', label: t.ui.typeTel },
          { value: 'sms', label: t.ui.typeSms },
        ]}
        maw={320}
      />

      {renderFields()}

      <Text fw={600} mt="sm">
        {t.ui.style}
      </Text>
      <Group grow>
        <ColorInput label={t.ui.foreground} value={fg} onChange={setFg} format="hex" />
        <ColorInput label={t.ui.background} value={bg} onChange={setBg} format="hex" />
      </Group>
      <Group grow align="flex-end">
        <Select
          label={t.ui.dotStyle}
          value={dotStyle}
          onChange={(v) => v && setDotStyle(v as DotStyle)}
          allowDeselect={false}
          data={[
            { value: 'square', label: t.ui.dotSquare },
            { value: 'rounded', label: t.ui.dotRounded },
            { value: 'dots', label: t.ui.dotDots },
            { value: 'extra-rounded', label: t.ui.dotExtra },
          ]}
        />
        <Select
          label={t.ui.errorLevel}
          value={level}
          onChange={(v) => v && setLevel(v as Level)}
          allowDeselect={false}
          disabled={!!logo}
          data={['L', 'M', 'Q', 'H'].map((l) => ({ value: l, label: l }))}
        />
      </Group>
      <div>
        <Group justify="space-between" mb={4}>
          <Text fw={500} size="sm">
            {t.ui.size}
          </Text>
          <Text fw={700} c="cyan" size="sm">
            {size}px
          </Text>
        </Group>
        <Slider min={128} max={512} step={32} value={size} onChange={setSize} aria-label={t.ui.size} />
      </div>

      <div>
        <Text fw={500} size="sm" mb={4}>
          {t.ui.logo}
        </Text>
        {logo ? (
          <Group>
            <img src={logo} alt="" height={48} style={{ borderRadius: 8 }} />
            <ActionIcon variant="subtle" color="red" onClick={() => setLogo(null)} aria-label={t.ui.logoRemove}>
              <TrashIcon size={18} />
            </ActionIcon>
          </Group>
        ) : (
          <Dropzone onDrop={(files) => files[0] && onLogo(files[0])} accept={IMAGE_MIME_TYPE} multiple={false} radius="md">
            <Group justify="center" gap="sm" mih={70} style={{ pointerEvents: 'none' }}>
              <ImageIcon size={24} color="var(--primary)" />
              <Text size="sm" c="dimmed">
                {t.ui.logoDrop}
              </Text>
            </Group>
          </Dropzone>
        )}
        {logo && (
          <Text size="xs" c="dimmed" mt={4}>
            {t.ui.logoHint}
          </Text>
        )}
      </div>

      {hasData ? (
        <Stack gap="md" align="center">
          <Paper radius="md" p="md" style={{ background: 'var(--white)', width: 'fit-content' }}>
            <div ref={containerRef} />
          </Paper>
          <Group>
            <Button variant="default" leftSection={<DownloadSimpleIcon size={16} />} onClick={() => download('png')}>
              PNG
            </Button>
            <Button variant="default" leftSection={<DownloadSimpleIcon size={16} />} onClick={() => download('svg')}>
              SVG
            </Button>
          </Group>
        </Stack>
      ) : (
        <Text c="dimmed" ta="center" py="xl">
          {t.ui.empty}
        </Text>
      )}
    </Stack>
  );
}
